import React from 'react'
import clsx from 'clsx'

import OpenConversationsV2 from '../../molecules/EHAccordion/OpenConversationsV2'
import Satisfaction from '../../molecules/Accordion/Satisfaction'
import TagBlockV2 from '../../molecules/EHAccordion/TagBlockV2'
import Accordion from '@SonarCX/components/atoms/Accordion'
import Profile from './Profile'
import NotesV2 from '../../molecules/EHAccordion/NotesV2'
import TasksV2 from '../../molecules/EHAccordion/TasksV2'
import ProfileV2 from '../../molecules/EHAccordion/ProfileV2'

import { useQueueState } from '../../../providers/QueueStateProvider'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useOwner } from '../../../providers/OwnerProvider'
import { useTaskAPI } from '../../../providers/TaskAPIProvider'
import { useBreakpoint } from '../../../providers/BreakpointProvider'
import CircleLeftIcon from '../../icons/CircleLeftIcon'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import {
  selectConvo,
  updateEngagementHubVisibility
} from '../../../slices/convo'
import useStyles from './styles'

type EngagementHubV2Props = {
  openMergeModal: () => void
  isOnLeft?: Boolean
  convoOverride?: any
}

const EngagementHubV2: React.FC<EngagementHubV2Props> = ({
  openMergeModal,
  isOnLeft,
  convoOverride
}) => {
  const dispatch = useAppDispatch()
  const { conversation } = useQueueState()
  const { allUsers } = useUserAPI()
  const { owner } = useOwner()
  const { loadTask } = useTaskAPI()
  const { down1025 } = useBreakpoint()
  const { hideEngagementHub } = useAppSelector(selectConvo)

  const showCollapsible = React.useMemo(
    () => !isOnLeft && down1025,
    [isOnLeft, down1025]
  )
  const convo = React.useMemo(
    () => convoOverride ?? (conversation?.owner_id ? conversation : {}),
    [convoOverride, conversation]
  )
  const hasOwner = React.useMemo(() => Boolean(convo?.owner_id), [convo])

  const toggleVisibility = React.useCallback(() => {
    dispatch(updateEngagementHubVisibility(!hideEngagementHub))
  }, [dispatch, hideEngagementHub])

  const classes = useStyles({
    open: isOnLeft ? true : !hideEngagementHub
  })

  // Load tasks if needed
  React.useEffect(() => {
    if (owner?.tasks) {
      loadTask(owner.tasks.map(task => task.id))
    }
  }, [owner, loadTask])

  return (
    <div className={clsx(classes.root, isOnLeft && classes.rootLeft)}>
      {showCollapsible && (
        <CircleLeftIcon
          data-testid='open-close-eh-button'
          onClick={toggleVisibility}
          className={classes.button}
        />
      )}
      <Profile
        conversation={convoOverride ?? conversation}
        allUsers={allUsers}
        owner={owner}
        hasOwner={hasOwner}
        openMergeModal={openMergeModal}
      />

      {(convoOverride ?? conversation) && (
        <div className={classes.accordionRoot}>
          <Accordion
            title='Recent Conversations'
            content={<OpenConversationsV2 user={owner} />}
          />

          <Accordion
            title='Profile'
            content={<ProfileV2 user={owner} convoOverride={convoOverride} />}
          />

          <Accordion title='Tags' content={<TagBlockV2 user={owner} />} />

          <Accordion title='Notes' content={<NotesV2 user={owner} />} />

          <Accordion
            title='Tasks'
            content={<TasksV2 user={owner} allUsers={allUsers} />}
          />

          <Accordion
            title='Rating'
            content={<Satisfaction user={owner} v2 />}
          />
        </div>
      )}
    </div>
  )
}

export default EngagementHubV2
